import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import { CircularProgress } from "@mui/material";
import Button from "../../../components/common/Button/Button";
import FormInput from "../../../components/common/FormInput/FormInput";
import UploadImage from "../../../components/common/UploadImage/UplaodImage";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";
import {
  getCustomShowGallery,
  updateCustomPageGallery
} from "../../../api/service/custompage.service";

const UpdateCustomPageGallery = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    page_id: "",
    title: "",
    description: "",
    slug: "",
    category: "",
    page_gallery_id: "",
  });
  const [image, setImage] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchGalleryData = async () => {
      if (!id) {
        showToast.error("Gallery ID is missing");
        navigate(-1);
        return;
      }

      try {
        setFetchLoading(true);
        const response = await getCustomShowGallery(id);

        if (response.success && response.data) {
          const galleryData = response.data;

          setFormData({
            page_id: galleryData.page_id || "",
            title: galleryData.title || "",
            description: galleryData.description || "",
            slug: galleryData.slug || "",
            category: galleryData.category || "",
            page_gallery_id: id,
          });

          // ✅ Set existing image URL if available
          if (galleryData.image?.url) {
            setExistingImage(galleryData.image.url);
          }
        } else {
          showToast.error(response.message || "Failed to fetch gallery data");
          navigate(-1);
        }
      } catch (error) {
        console.error("Fetch Gallery Error:", error);
        showToast.error(
          error?.response?.data?.message || "Failed to fetch gallery data"
        );
        navigate(-1);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchGalleryData();
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (file) => {
    setImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.page_gallery_id) {
      showToast.error("Gallery ID is missing");
      return;
    }

    if (!formData.page_id) {
      showToast.error("Page ID is missing");
      return;
    }

    if (!formData.title.trim()) {
      showToast.error("Please enter title");
      return;
    }

    if (!formData.description.trim()) {
      showToast.error("Please enter description");
      return;
    }

    if (!formData.category.trim()) {
      showToast.error("Please enter category");
      return;
    }

    try {
      setLoading(true);
      const submitData = new FormData();
      submitData.append("page_gallery_id", formData.page_gallery_id);
      submitData.append("page_id", formData.page_id);
      submitData.append("title", formData.title.trim());
      submitData.append("description", formData.description.trim());
      submitData.append("slug", formData.slug || "");
      submitData.append("category", formData.category.trim());

      if (image) {
        submitData.append("image", image);
      }

      const response = await updateCustomPageGallery(submitData);

      if (response.success) {
        showToast.success(response.message || "Custom Gallery updated successfully!");
        navigate(-1);
      } else {
        showToast.error(response.message || "Failed to update gallery");
      }
    } catch (error) {
      console.error("Update Gallery Error:", error);
      showToast.error(
        error?.response?.data?.message || "Failed to update gallery"
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ MUI CircularProgress loader (black color)
  if (fetchLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CircularProgress sx={{ color: "#000" }} />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3">
        <FaArrowLeftLong
          style={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
        <h3 className="management-tile mb-0">Update Custom Gallery</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} className="mb-3">
            <label className="form-label">
              Title <span style={{ color: "red" }}>*</span>
            </label>
            <FormInput
              name="title"
              value={formData.title}
              placeholder="Enter title"
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">
              Description <span style={{ color: "red" }}>*</span>
            </label>
            <FormInput
              type="textarea"
              name="description"
              value={formData.description}
              placeholder="Enter description"
              onChange={handleInputChange}
              rows={4}
              required
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">Slug</label>
            <FormInput
              name="slug"
              value={formData.slug}
              placeholder="Enter slug (optional)"
              onChange={handleInputChange}
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">
              Category <span style={{ color: "red" }}>*</span>
            </label>
            <FormInput
              name="category"
              value={formData.category}
              placeholder="Enter category"
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">
              Image {!existingImage && <span style={{ color: "red" }}>*</span>}
            </label>
            <UploadImage
              onFileSelect={handleFileSelect}
              existingImages={existingImage}
            />
          </Col>

          <Col xs={12}>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update Gallery"}
            </Button>
          </Col>
        </Row>
      </form>
    </>
  );
};

export default UpdateCustomPageGallery;